import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { set, isEqual as _isEqual } from 'date-fns';

const defaultLocale = 'no';
const defaultTimeZone = 'Europe/Oslo';

/**
 *
 * @param left first Date to compare
 * @param right second Date to compare
 * @param unit
 * @returns
 */
export function isEqual(
    left: Date | number,
    right: Date | number,
    unit: 'year' | 'month' | 'date' | 'hour' | 'minute' | 'second' | 'millisecond' = 'second'
) {
    if (!left || !right) return false;

    let setOptions = {};
    switch (unit) {
        case 'year':
            setOptions = {
                month: 1,
                date: 1,
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0
            };
            break;
        case 'month':
            setOptions = {
                date: 1,
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0
            };
            break;
        case 'date':
            setOptions = {
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0
            };
            break;
        case 'hour':
            setOptions = {
                minutes: 0,
                seconds: 0,
                milliseconds: 0
            };
            break;
        case 'minute':
            setOptions = {
                seconds: 0,
                milliseconds: 0
            };
            break;
        case 'second':
            setOptions = {
                milliseconds: 0
            };
            break;
    }

    return _isEqual(set(left, setOptions), set(right, setOptions));
}

/**
 * Converts Date object from UTC to given timezone.
 * @param utcDate UTC Date object or ISO Date string
 * @param timeZone IANA time zone name
 * @returns Date object representing local time in the given timezone
 */
export function toLocalDate(utcDate: Date | number, timeZone = defaultTimeZone) {
    return toZonedTime(utcDate, timeZone);
}

/**
 * Converts Date object from given timezone to UTC.
 * @param localDate Date object or ISO Date string
 * @param timeZone IANA time zone name
 * @returns Date object representing UTC time
 */
export function toUtcDate(localDate: Date | number, timeZone = defaultTimeZone) {
    return fromZonedTime(localDate, timeZone);
}

export function toLocaleTimeString(date: Date | number, locale = defaultLocale, timeZone = defaultTimeZone) {
    if (!date) return '';

    return new Intl.DateTimeFormat(locale, {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).format(new Date(date));
}

export function toLocaleLongDateString(date: Date | number, locale = defaultLocale, timeZone = defaultTimeZone) {
    if (!date) return '';

    return new Intl.DateTimeFormat(locale, {
        timeZone,
        weekday: 'long',
        day: 'numeric',
        month: 'long'
    }).format(new Date(date));
}

export function toLocaleDateString(date: Date | number, locale = defaultLocale, timeZone = defaultTimeZone) {
    if (!date) return '';

    return new Intl.DateTimeFormat(locale, {
        timeZone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(new Date(date));
}

export function humanizeDuration(seconds: number, units: { [key: string]: string }): string {
    if (typeof seconds !== 'number') return '';

    let minutes = Math.ceil(seconds / 60);
    if (minutes === 0) minutes = 0;
    else if (minutes > 0 && minutes < 1) minutes = 1;

    if (minutes < 60) return `${minutes} ${units['minutes']}`;
    if (minutes == 60) return `1 ${units['hours']}`;

    return `${Math.floor(minutes / 60)} ${units['hours']} ${minutes - Math.floor(minutes / 60) * 60} ${units['minutes']}`;
}

export function useDateHelpers() {
    return {
        isEqual,
        toLocalDate,
        toUtcDate,
        toLocaleTimeString,
        toLocaleDateString,
        toLocaleLongDateString,
        humanizeDuration
    };
}
